import { APP_INITIALIZER, ApplicationConfig } from "@angular/core";
import { provideRouter, withComponentInputBinding, withRouterConfig } from "@angular/router";
import { routes } from "./app.routes";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideHttpClient, withInterceptorsFromDi, withFetch, HTTP_INTERCEPTORS } from "@angular/common/http";
import { AccountsService } from "./shared/services/accounts.service";
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService,
    ProtectedResourceScopes
} from "@azure/msal-angular";
import { IPublicClientApplication, PublicClientApplication, InteractionType } from "@azure/msal-browser";
import { LogLevel, Configuration, BrowserCacheLocation } from "@azure/msal-browser";
import { environment } from "../environments/environment";
import { provideMatomo, withRouter } from "ngx-matomo-client";
import { HttpErrorInterceptor } from "./http-error.interceptor";

//const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;
const isTestCafe = typeof window !== "undefined" && ("%testCafeAutomation%" in window || window.location.search.includes("testcafe=1"));

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication(msalConfig);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string | ProtectedResourceScopes> | null>();
    return {
        interactionType: isTestCafe ? InteractionType.Redirect : InteractionType.Popup,
        protectedResourceMap
    };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: isTestCafe ? InteractionType.Redirect : InteractionType.Popup,
        authRequest: {
            scopes: [...environment.apiConfig.scopes]
        }
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(
            routes,
            withComponentInputBinding(),
            withRouterConfig({
                onSameUrlNavigation: "reload"
            })
        ),
        provideMatomo(
            {
                siteId: environment.matomoSettings.siteId,
                trackerUrl: environment.matomoSettings.trackerUrl
            },
            withRouter()
        ),
        provideAnimations(),
        provideHttpClient(withInterceptorsFromDi(), withFetch()),
        AccountsService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
        MsalService,
        MsalGuard,
        MsalBroadcastService
    ]
};

export const msalConfig: Configuration = {
    auth: {
        clientId: environment.msalConfig.auth.clientId, // This is the ONLY mandatory field that you need to supply.
        authority: environment.b2cPolicies.authorities.signUpSignIn.authority, // Defaults to "https://login.microsoftonline.com/common"
        knownAuthorities: [environment.b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        redirectUri: "/", // Points to window.location.origin by default. You must register this URI on Azure portal/App Registration.
        postLogoutRedirectUri: "/" // Points to window.location.origin by default.
    },
    cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
        loggerOptions: {
            loggerCallback(logLevel: LogLevel, message: string) {
                return { logLevel, message };
            },
            logLevel: LogLevel.Trace,
            piiLoggingEnabled: false
        }
    }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: []
};
